import React, { ReactNode, useEffect, useState } from "react";
import { navigate } from "gatsby";
import Header from "./Header";
import Footer from "./Footer";
import { AuthProvider, useAuth } from "../contexts/AuthContext";

interface AdminLayoutProps {
  children: ReactNode;
}

// This component checks if the user is authenticated and redirects to login if not
const ProtectedRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // `mounted` is false during SSR/SSG and the very first client render, so the
  // protected children are never emitted into the static HTML or the initial
  // paint. Without this, a logged-out visitor briefly sees the dashboard on the
  // first hit to /admin before the auth check and redirect run.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && !loading && !isAuthenticated) {
      navigate("/login");
    }
  }, [mounted, isAuthenticated, loading]);

  if (!mounted || loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return isAuthenticated ? <>{children}</> : null;
};

// The AdminLayout wraps content with AuthProvider and ProtectedRoute
const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  return (
    <AuthProvider>
      <div className="flex flex-col min-h-screen">
        <main className="flex-grow">
          <ProtectedRoute>{children}</ProtectedRoute>
        </main>
      </div>
    </AuthProvider>
  );
};

export default AdminLayout;
